/**
 * CitationPanel Component
 * Displays citation suggestions and allows insertion into document
 */

import React, { useState } from 'react'
import { logError } from '@utils/logger'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  BookOpenIcon,
  LinkIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  BookmarkIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

import type { CitationPaper } from '@/api/citations'
import { citationLibraryAPI } from '@/api/citationLibrary'
import { generateBibtexEntry, generateBibtexKey as generateBibtexKeyUtil } from '@/utils/generateBibtex'

interface CitationPanelProps {
  isOpen: boolean
  onClose: () => void
  suggestions: CitationPaper[]
  isLoading: boolean
  selectedText: string
  onCitationInsert?: (citation: string, paper?: CitationPaper) => void
  citationStyle?: 'apa' | 'mla' | 'chicago' | 'ieee' | 'bibtex'
  documentMode?: 'markdown' | 'latex' | 'plain'
  documentId?: string | null
}

export const CitationPanel: React.FC<CitationPanelProps> = ({
  isOpen,
  onClose,
  suggestions,
  isLoading,
  selectedText,
  onCitationInsert,
  citationStyle = 'apa',
  documentMode = 'markdown',
  documentId, // Will be used for citation library
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [isMinimized, setIsMinimized] = useState(false)
  const isLaTeX = documentMode === 'latex'

  // Generate BibTeX citation key
  const generateBibtexKey = (paper: CitationPaper): string => {
    return generateBibtexKeyUtil(paper)
  }

  const formatCitation = (paper: CitationPaper, style: string = citationStyle): string => {
    switch (style) {
      case 'apa':
        const authors = paper.authors.length > 0
          ? paper.authors.length === 1
            ? paper.authors[0]
            : paper.authors.length <= 6
            ? paper.authors.join(', ')
            : `${paper.authors.slice(0, 5).join(', ')}, et al.`
          : 'Unknown Author'
        const year = paper.year ? ` (${paper.year}).` : '.'
        return `${authors}${year} ${paper.title}. ${paper.venue ? paper.venue + '. ' : ''}${paper.doi ? `https://doi.org/${paper.doi}` : paper.url || ''}`
      
      case 'bibtex':
        const bibKey = paper.doi?.replace(/[^a-zA-Z0-9]/g, '') || 
                      paper.title.toLowerCase().replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)
        return `@article{${bibKey},
  title={${paper.title}},
  author={${paper.authors.join(' and ')}},
  year={${paper.year || 'n.d.'}},
  ${paper.doi ? `doi={${paper.doi}},` : ''}
  ${paper.venue ? `journal={${paper.venue}},` : ''}
}`
      
      case 'ieee':
        const ieeeAuthors = paper.authors.length > 0
          ? paper.authors.length === 1
            ? paper.authors[0]
            : paper.authors.length <= 6
            ? paper.authors.join(', ')
            : `${paper.authors.slice(0, 5).join(', ')}, et al.`
          : 'Unknown Author'
        return `${ieeeAuthors}, "${paper.title}," ${paper.venue || 'Journal'}, ${paper.year || 'n.d.'}.`
      
      default: // MLA
        const mlaAuthors = paper.authors.length > 0
          ? paper.authors.length === 1
            ? paper.authors[0]
            : paper.authors.join(', ')
          : 'Unknown Author'
        return `${mlaAuthors}. "${paper.title}." ${paper.venue || 'Journal'}, ${paper.year || 'n.d.'}.`
    }
  }

  const handleInsertCitation = (paper: CitationPaper) => {
    if (isLaTeX) {
      // For LaTeX, generate BibTeX key and pass paper object
      const bibtexKey = generateBibtexKey(paper)
      // Add bibtexKey to paper object for use in insertion
      const paperWithKey = { ...paper, bibtexKey }
      onCitationInsert?.('', paperWithKey)
    } else {
      // For non-LaTeX documents, insert formatted citation text
      const citation = formatCitation(paper)
      onCitationInsert?.(citation, paper)
    }
    // Don't close on insert - just minimize so user can insert more citations
    setIsMinimized(true)
  }

  const handleCopyCitation = (paper: CitationPaper) => {
    const citation = formatCitation(paper)
    navigator.clipboard.writeText(citation)
    setCopiedId(paper.doi || paper.title)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleSaveToLibrary = async (paper: CitationPaper) => {
    if (!documentId) {
      return
    }

    const paperId = paper.doi || paper.title || ''
    setSavingId(paperId)

    try {
      // Generate BibTeX entry for LaTeX documents
      let bibtexEntry: string | undefined
      let bibtexKey: string | undefined
      
      if (isLaTeX) {
        bibtexKey = generateBibtexKey(paper)
        bibtexEntry = generateBibtexEntry(paper, bibtexKey)
      }

      await citationLibraryAPI.saveCitation(documentId, {
        title: paper.title,
        authors: paper.authors,
        year: paper.year,
        doi: paper.doi,
        pmid: paper.pmid,
        arxiv_id: paper.arxiv_id,
        abstract: paper.abstract,
        venue: paper.venue,
        url: paper.url,
        bibtex_key: bibtexKey,
        bibtex_entry: bibtexEntry,
      })

    } catch (error) {
      logError('Error saving citation:', error)
    } finally {
      setSavingId(null)
    }
  }


  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className={`fixed z-50 pointer-events-none ${
        isMinimized 
          ? 'bottom-4 left-1/2 transform -translate-x-1/2 w-full max-w-3xl px-4' 
          : 'inset-0 flex items-center justify-center'
      }`}>
        {/* Backdrop - only show when not minimized */}
        {!isMinimized && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMinimized(true)}
            className="absolute inset-0 bg-black/50 pointer-events-auto z-0"
          />
        )}

        {/* Panel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: 0
          }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()} // Prevent clicks inside panel from bubbling to backdrop
          onMouseDown={(e) => e.stopPropagation()} // Also prevent mousedown events
          className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full flex flex-col pointer-events-auto transition-all duration-300 z-10 ${
            isMinimized 
              ? 'max-h-[60px] overflow-hidden' 
              : 'max-w-3xl mx-4 max-h-[80vh]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <BookOpenIcon className="w-6 h-6 text-amber-600" />
              {!isMinimized && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Citation Suggestions
                  </h2>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {suggestions.length} relevant papers found
                    </p>
                    {isLaTeX && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        LaTeX Mode
                      </span>
                    )}
                  </div>
                </div>
              )}
              {isMinimized && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Citation Suggestions ({suggestions.length} papers)
                  </h2>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={isMinimized ? 'Maximize' : 'Minimize'}
              >
                {isMinimized ? (
                  <ChevronUpIcon className="w-5 h-5" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Selected Text Preview */}
          {!isMinimized && (
            <>
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Selected text:</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic line-clamp-2">
                  {selectedText.length > 150 ? `${selectedText.substring(0, 150)}...` : selectedText}
                </p>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <ArrowPathIcon className="w-12 h-12 text-amber-600 animate-spin mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  Analyzing text and searching for relevant papers...
                </p>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <BookOpenIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                <p className="text-gray-600 dark:text-gray-400 text-center">
                  No citations found. Try selecting different text or adjusting your search.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {suggestions.map((paper, index) => (
                  <motion.div
                    key={paper.doi || paper.title || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-4 transition-all border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-700"
                  >
                    {/* Paper Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          {paper.title}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <span>{paper.authors.slice(0, 3).join(', ')}{paper.authors.length > 3 ? ' et al.' : ''}</span>
                          {paper.year && <span>• {paper.year}</span>}
                          {paper.venue && <span>• {paper.venue}</span>}
                        </div>
                      </div>
                      
                      {/* Relevance Score */}
                      {paper.relevance_score && (
                        <div className="ml-4 flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded text-xs font-medium text-amber-700 dark:text-amber-300">
                          <span>★</span>
                          <span>{paper.relevance_score.toFixed(1)}/5</span>
                        </div>
                      )}
                    </div>

                    {/* Abstract */}
                    {paper.abstract && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                        {paper.abstract}
                      </p>
                    )}

                    {/* Relevance Explanation */}
                    {paper.relevance_explanation && (
                      <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs text-blue-800 dark:text-blue-200">
                        <strong>Why relevant:</strong> {paper.relevance_explanation}
                      </div>
                    )}

                    {/* Identifiers */}
                    <div className="flex items-center gap-4 mb-3 text-xs text-gray-500 dark:text-gray-400">
                      {paper.doi && (
                        <span className="flex items-center gap-1">
                          <LinkIcon className="w-3 h-3" />
                          DOI: {paper.doi}
                        </span>
                      )}
                      {paper.pmid && (
                        <span>PMID: {paper.pmid}</span>
                      )}
                      {paper.arxiv_id && (
                        <span>arXiv: {paper.arxiv_id}</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => handleInsertCitation(paper)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
                      >
                        <CheckIcon className="w-4 h-4" />
                        {isLaTeX ? 'Insert \\cite{}' : 'Insert Citation'}
                      </button>
                      {documentId && (
                        <button
                          onClick={() => handleSaveToLibrary(paper)}
                          disabled={savingId === (paper.doi || paper.title)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingId === (paper.doi || paper.title) ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <BookmarkIcon className="w-4 h-4" />
                              Save to Library
                            </>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleCopyCitation(paper)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        {copiedId === (paper.doi || paper.title) ? (
                          <>
                            <CheckIcon className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <ClipboardDocumentIcon className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </button>
                      {paper.url && (
                        <a
                          href={paper.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          View
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Citations are generated using AI and external academic databases. Please verify accuracy before use.
                </p>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

